import { CSV_EXTENSION, EXCEL_EXTENSION } from "../constants/app";
import { truncateText } from "../utils/strings";
import { CustomField, Day, Label, Latitude, LatLng, Longitude, Timestamp, Uid } from "./core";
import { FormContent, FormItemType } from "./form";
import type { JobGroupSummary } from "./jobGroup";
import { HOUR } from "../utils/time";
import { ALL_JOBS_GRID_DEFAULT_TYPE, ALL_JOBS_GRID_MEMBER_TYPE } from "../constants/jobs";

export const PROGRESS_STATUS_UNASSIGNED = 0;
export const PROGRESS_STATUS_SCHEDULED = 1;
export const PROGRESS_STATUS_ACCEPTED = 2;
export const PROGRESS_STATUS_IN_PROGRESS = 3;
export const PROGRESS_STATUS_SUCCESS = 4;
export const PROGRESS_STATUS_ISSUE = 5;
export const PROGRESS_STATUS_REJECTED = 6;

export const PRIO_HIGH = 9;
export const PRIO_NORMAL = 5;
export const PRIO_LOW = 2;
export const STATUS_ACTIVE = 'active';
export const STATUS_ARCHIVED = 'archived';
export const STATUS_DELETED = 'deleted';
export const NUMBER_LAST = 99;

export type JobStatus = typeof STATUS_ACTIVE | typeof STATUS_ARCHIVED | typeof STATUS_DELETED;

export enum JobTypes {
    WORK,
    PICKUP,
    DROPOFF,
}

/** Where and when the photo went up, and where and when it was taken when the phone knows that. */
export interface JobAttachmentMeta {
    ts: Timestamp;
    lat?: Latitude;
    lng?: Longitude;
    takenTs?: Timestamp;
    takenLat?: Latitude;
    takenLng?: Longitude;
}

export interface SubmittedVoizReport {
    submissionId: string;
    url: string;
    pdfUrl: string;
    printUrl: string;
    title: string;
    savedAt: Timestamp;
    userName: string;
    lat: Latitude;
    lng: Longitude;
    locationSource: string;
}

export interface Job {
    id: string;
    assigneeName: string;
    assigneeUsername: string;
    carryOver: number;
    confirmationEmails: string;
    contactEmail: string;
    contactName: string;
    contactPhone: string;
    customFields: CustomField[];
    day: Day;
    destinationLat: Latitude;
    destinationLng: Longitude;
    destinationName: string;
    destinationText: string;
    destinationUrl: string;
    dispatcherName: string;
    dispatcherUid: Uid;
    dispatcherUrl: string;
    inputFormConfig: string; // stringified FormContent, empty if using standard
    formConfig: string; // read only stringified FormContent, also set if standard
    isPartOfSequence?: boolean;
    isTemplate?: boolean;
    // templates only: how often the template was used, and when and by whom last
    useCount?: number;
    lastUsedAt?: Timestamp;
    lastUsedBy?: Uid;
    itemsToDropoff: number;
    itemsToPickup: number;
    linkedZones: string;
    linkedForms: string;
    linkedVoizReports?: string;
    manualChecks: string;
    number: number;
    onSiteSeconds: number;
    orderId: number;
    placeName: string;
    placeUid: Uid;
    placeUrl: string;
    priority: number;
    radius: number;
    receiverUid: Uid;
    requiredEquipment?: string;
    requiredSkills?: string;
    sequenceId?: string;
    signatureName: string;
    signaturePdf: string;
    signatureSvg: string;
    /** a list loads without signature drawings and says here which job has one; the job by id brings it */
    hasSignatureSvg?: boolean;
    status: JobStatus;
    submittedForms: string;
    submittedVoizReports?: string;
    teamId: number;
    textDispatcher: string;
    textReceiver: string;
    tsAccepted: Timestamp;
    tsAssigned: Timestamp;
    tsCheckIn: Timestamp;
    tsCheckOut: Timestamp;
    tsCreated: Timestamp;
    tsDoneFailed: Timestamp;
    tsDoneSuccess: Timestamp;
    tsRejected: Timestamp;
    tsSaved: Timestamp;
    tsScheduled: Timestamp;
    tsTripStarted: Timestamp;
    tsSignature: Timestamp;
    type: number;
    windowEnd: number;
    windowStart: number;
    worker: string;
    workerEmail: string;
    workerName: string;
    workerPhone: string;
    workerUsername: string;
    assigneeHistory?: string;
    progressStatusHistory?: string;
    createdBy?: Uid;
    createdVia?: string;
    associatedJobId?: string;
    jobGroupId?: string;
    jobGroupOrder?: number;
    jobGroupSummary?: JobGroupSummary;
    comments?: string;
    labels: string;
    tripId: string;
    [key: `url_${number}`]: string;
    [key: `url_meta_${number}`]: JobAttachmentMeta;
    [key: `extra_text_${number}_val`]: string;
    [key: `extra_number_${number}_val`]: number;
}

export function getJobReadOnlyFields(isNewJob: boolean) {
    const fields: (keyof Job)[] = [
        "tsDoneFailed",
        "tsDoneSuccess",
        "tsRejected",
        "tsSaved",
        "tsAccepted",
        "tsScheduled",
        "tsAssigned",
        "assigneeHistory",
        "progressStatusHistory"
    ];

    if (!isNewJob) {
        fields.push("number");
    }

    return fields;
}

export interface RouteInfo {
    src: "google" | "azure" | "solve" | "unknown";
    travelMode: "car" | "bicycle" | "pedestrian" | "truck" | "van" | "motorcycle";
    optimized: boolean;
    savedTs: Timestamp;
    routeTimeInSec: number;
    routeDistanceInKM: number;
    sequence: number[];
    legTimesInSec: number[];
    legWaitInSec: number[];
    /** the lunch break before each leg; only on a day with one, and missing from older runtimes */
    legBreakInSec?: number[];
    legDistancesInKM: number[];
    metaOnSiteTimesInSec: number[];
    metaStartTime: number;
    startLocation: LatLng;
    endLocation: LatLng;
    memberUid: Uid;
    skills: string[],
    equipment: string[],
}

export interface JobWithEtaEtd extends Job {
    // Pre-calculated timestamps for the job based on route info
    calculatedStartTs: Timestamp; // ETA - when job is expected to start
    calculatedEndTs: Timestamp;   // ETD - when job is expected to end
    hasRouteInfo: boolean;         // Whether these are calculated from route or fallback
}

export interface ConfirmationEmail {
    email: string;
    onSuccess: boolean;
}

export interface ManualCheck {
    ts: Timestamp;
    type: "checkin" | "checkout";
}

export interface AssigneeHistoryEntry {
    ts: Timestamp;
    assigneeUid: Uid;
    dispatcherUid: Uid;
    day?: Day;
    number?: number;
}

export interface ProgressStatusHistoryEntry {
    ts: Timestamp;
    status: typeof PROGRESS_STATUS_UNASSIGNED
        | typeof PROGRESS_STATUS_SCHEDULED
        | typeof PROGRESS_STATUS_ACCEPTED
        | typeof PROGRESS_STATUS_IN_PROGRESS
        | typeof PROGRESS_STATUS_SUCCESS
        | typeof PROGRESS_STATUS_ISSUE
        | typeof PROGRESS_STATUS_REJECTED;
    uid: Uid;
}

export type ImportJobFileFormat = typeof CSV_EXTENSION | typeof EXCEL_EXTENSION;

export type AllJobsGridType = typeof ALL_JOBS_GRID_DEFAULT_TYPE | typeof ALL_JOBS_GRID_MEMBER_TYPE;

export function getConfirmationEmails(confirmationEmails: string): ConfirmationEmail[] {
    const plain = (confirmationEmails || "").trim();
    if (!plain) {
        return [];
    }
    // The API also accepts a plain comma list ("a@x.com,b@y.com"); the runtime reads both.
    if (plain.startsWith("[")) {
        try {
            const parsed = JSON.parse(plain);
            return Array.isArray(parsed) ? parsed.filter(ce => ce?.email) : [];
        } catch {
            return [];
        }
    }
    return plain.split(",")
        .map(email => email.trim())
        .filter(Boolean)
        .map(email => ({ email, onSuccess: true }));
}

export function getDoneTs(job: Job): number {
    return Math.max(job.tsDoneFailed, job.tsDoneSuccess);
}

export function isDone(job: Job): boolean {
    return getDoneTs(job) > job.tsScheduled
}

export function isSuccess(job: Job): boolean {
    return isDone(job) && job.tsDoneSuccess > job.tsDoneFailed;
}

export function isFailed(job: Job): boolean {
    return job.tsDoneFailed > job.tsDoneSuccess && job.tsDoneFailed > job.tsScheduled;
}

/** The assignee turned the job down and nobody has rescheduled or reassigned it since. */
export function isRejected(job: Job): boolean {
    return job.tsRejected > job.tsAccepted && job.tsRejected >= job.tsScheduled;
}

export function isAccepted(job: Job): boolean {
    return job.tsAccepted > job.tsRejected && job.tsAccepted >= job.tsScheduled;
}

export function isHighPriority(job: Job): boolean {
    return job.priority > PRIO_NORMAL;
}

export function isLowPriority(job: Job): boolean {
    return job.priority < PRIO_NORMAL;
}

export function hasAnyPriority(job: Job): boolean {
    return isHighPriority(job) || isLowPriority(job);
}

export function isCheckedIn(job: Job): boolean {
    return job.tsCheckIn > 0;
}

export function isCheckedOut(job: Job): boolean {
    return job.tsCheckOut > 0;
}

export function isAssigned(job: Job): boolean {
    return job.receiverUid !== '';
}

export function getTitle(job: Job, max = 100): string {
    const title = job?.destinationName || job.destinationText || "-";
    return title.length > max ? truncateText(title, max) : title;
}

export function hasSignature(job: Job): boolean {
    return job.signaturePdf || job.signatureSvg || job.hasSignatureSvg ? true : false;
}

/** A list comes without signature drawings: the job keeps the drawing already loaded for the same signature. */
export function withKnownSignatureSvg(incoming: Job, known: Job | undefined): Job {
    if (!incoming.hasSignatureSvg || incoming.signatureSvg || !known?.signatureSvg || known.tsSignature !== incoming.tsSignature) {
        return incoming;
    }
    return { ...incoming, signatureSvg: known.signatureSvg };
}

export function getManualChecks(job: Job): ManualCheck[] {
    if (job.manualChecks) {
        const json = JSON.parse(job.manualChecks);
        return json.checks as ManualCheck[];
    } else {
        return [] as ManualCheck[];
    }
}

export function getInputFormConfig(job: Job): FormContent | undefined {
    let formContent: FormContent | undefined;
    
    if (job?.inputFormConfig) {
        formContent = JSON.parse(job.inputFormConfig) as FormContent;
    } else if (job?.formConfig) {
        formContent = JSON.parse(job.formConfig) as FormContent;
    } else {
        return undefined;
    }
    
    let textDecimalIndex = 0;
    let numberCheckboxIndex = 0;
    
    // Add type-specific indices to form items
    if (formContent?.content) {
        formContent.content = formContent.content.map(item => {
            if (item.type === "number" || item.type === "checkbox") {
                numberCheckboxIndex++;
                return { ...item, index: numberCheckboxIndex };
            } else {
                // For text, decimal, or other types
                textDecimalIndex++;
                return { ...item, index: textDecimalIndex };
            }
        });
    }
    
    return formContent;
}

// Form item types whose answers are stored on the job as extra_*_val fields
// (the built-in "Staff Input" form). Display-only types (titles, sections,
// labels, images, signatures, files) are intentionally excluded.
export const FORM_FIELD_INPUT_TYPES: FormItemType[] = [
    "text",
    "paragraph",
    "number",
    "decimal",
    "checkbox",
    "radio",
    "dropdown",
];

/**
 * Reads a single built-in form answer off a job by the form field's display name.
 *
 * The job stores answers as flat keys (`extra_number_{i}_val` for number/checkbox,
 * `extra_text_{i}_val` for everything else) where `i` is derived per-job by
 * {@link getInputFormConfig}. Resolving by name (rather than index) keeps this
 * correct across both the company standard form and per-job individual forms.
 */
export function getJobFormFieldValue(job: Job, fieldName: string): string | number | undefined {
    const config = getInputFormConfig(job);
    const item = config?.content?.find(
        (i) => i.name?.trim() === fieldName && FORM_FIELD_INPUT_TYPES.includes(i.type),
    );
    if (!item || item.index === undefined) {
        return undefined;
    }
    const isNumeric = item.type === "number" || item.type === "checkbox";
    const key = isNumeric ? `extra_number_${item.index}_val` : `extra_text_${item.index}_val`;
    const value = (job as Job & Record<string, unknown>)[key];
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    return value as string | number;
}

export function getMostRecentTimestamp(job: Job): number {
    return Math.max(job.tsSaved, job.tsCreated, job.tsAssigned, job.tsAccepted, job.tsScheduled, job.tsCheckIn, job.tsDoneFailed, job.tsDoneSuccess);
}

export function isRecentlyUpdated(job: Job) {
    return Date.now() - getMostRecentTimestamp(job) < HOUR;
}

export function getLabels(job?: Job) {
    let labels: Label[];
    try {
        labels = JSON.parse(job?.labels || "[]") as Label[];
        if (!labels) {
            labels = [];
        }
    } catch (error) {
        console.error("Failed to parse labels:", error);
        labels = [];
    }
    return labels;
}

export function isPickup(job: Job) {
    return job.type === JobTypes.PICKUP;
}

export function isDropoff(job: Job) {
    return job.type === JobTypes.DROPOFF;
}

export function isPickupOrDropoff(job: Job) {
    return job.type === JobTypes.PICKUP || job.type === JobTypes.DROPOFF;
}

export function getCustomField(job: Job, key: string) {
    return job.customFields.find((customField) => customField.key === key);
}

/** A job the company keeps making by hand: same title at the same place. "Again" clones jobId. */
export type JobPattern = {
    jobId: string;
    title: string;
    address: string;
    placeUid: Uid | "";
    type: string;
    count: number;
    lastDay: Day;
    lastAssigneeUid: Uid | "";
    lastCreatedTs: Timestamp;
};

/** What the company's history knows about a place or a point, for the New job card. */
export type LocationInsights = {
    jobCount: number;
    titles: { title: string; type: number; count: number }[];
    visitSeconds: number; // median of real visits, 0 below two
    visitCount: number;
    lastAssignees: Uid[];
    openJobs: { jobId: string; title: string; day: Day; assigneeUid: Uid | "" }[];
    /** open jobs at the point without a place: what a new place here would take over */
    unplacedOpenJobIds?: string[];
};

/** Where a draft job would fit into a person's route that day. */
export type AssigneeSuggestion = {
    uid: Uid;
    detourSec: number;
    etaSec: number; // seconds into the day, about
    number: number; // route number the job takes there
    jobCount: number;
    off: boolean;
    afterTitle: string;
};

/** A stop of a route that is not a job yet. */
export type DraftRouteStop = {
    key: string;
    title: string;
    lat: number;
    lng: number;
    onSiteSeconds: number;
    windowStart?: number;
    windowEnd?: number;
};

/** A person's day with the draft stops in it, as the runtime works it out without saving anything. */
export type DraftRoutePlan = {
    ok: boolean;
    reason: string;
    // job ids and draft keys in route order, with arrival and departure in seconds into the day
    // lunchSec/lunchAtSec: the lunch break on the way to that stop, only where there is one
    order: { key: string; draft: boolean; eta: number; etd: number; late: boolean; lunchSec?: number; lunchAtSec?: number }[];
    driveSec: number;
    distanceKm: number;
    startSec: number;
    endSec: number;
    /** 0 when the person has no working hours that day; missing from older runtimes */
    shiftStartSec?: number;
    shiftEndSec: number; // 0 when the person has no working hours that day
    /** the person has no working hours that day; missing from older runtimes */
    noHours?: boolean;
    path: [number, number][]; // [lng, lat]
    /** the same line cut into its legs, start → first stop … last stop → end; empty from older runtimes */
    legs?: [number, number][][];
    unfit: string[];
    noLocation: string[];
    /** when the solver left stops out: the order that places them too, once what kept them out is loosened */
    relaxed?: RelaxedRoutePlan;
};

/**
 * What kept a stop out of the solver's order and was loosened to place it: the working hours, a skill the person
 * lacks, more items than the vehicle takes, or the stop's window.
 */
export type Loosened = "hours" | "skill" | "capacity" | "window";

/**
 * The order with every stop placed, and for each stop that was loosened, what was: its main reason, and every
 * issue (a skill and the load both, when both apply; older runtimes send only the reasons).
 */
export type RelaxedRoutePlan = DraftRoutePlan & { reasons: Record<string, Loosened>; issues?: Record<string, Loosened[]> };
